import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useQuestions } from "@/lib/useInsights";
import { AREAS, LEVELS } from "@/lib/heat";
import { RATING_SCALE } from "@/lib/topics";
import { ResourceNotice, PrintBar } from "@/components/ResourceNotice";
import { useResourcePrint } from "@/pages/ColoringSheet";

const ranges = ["0–11", "12–20", "21–28", "29–36"];

export const Question = ({ n, q, desc }) => (
  <div className="avoid-break border-b border-slate-200 py-3 print:py-1.5" data-testid={`diy-question-${q.key}`}>
    <div className="flex items-baseline gap-2">
      <span className="font-mono text-xs text-slate-400">{n}.</span>
      <span className="font-display text-base font-semibold text-slate-900">{q.label}</span>
    </div>
    {desc && <p className="mt-1 text-xs leading-relaxed text-slate-600">{desc}</p>}
    <div className="mt-2 grid grid-cols-5 gap-1.5">
      {RATING_SCALE.map((r, i) => (
        <div key={r.short} className="flex items-start gap-1.5 rounded border border-slate-200 px-1.5 py-1 text-[10px] leading-tight text-slate-700">
          <span className="mt-0.5 inline-block h-3 w-3 shrink-0 rounded-sm border border-slate-500" />
          <span>{r.full} <span className="font-mono text-slate-400">({i})</span></span>
        </div>
      ))}
    </div>
  </div>
);

export const ScoringTable = ({ questions }) => (
  <table className="mt-3 w-full border-collapse text-xs print:text-[10px] print:[&_td]:py-0 print:[&_th]:py-0.5" data-testid="diy-scoring-table">
    <thead>
      <tr className="bg-slate-100 text-left">
        <th className="border border-slate-300 px-2 py-1">#</th>
        <th className="border border-slate-300 px-2 py-1">Topic</th>
        <th className="border border-slate-300 px-2 py-1 text-center">Points (0–4)</th>
        {AREAS.map((a) => <th key={a.key} className="border border-slate-300 px-2 py-1 text-center">{a.label}</th>)}
      </tr>
    </thead>
    <tbody>
      {questions.map((q, i) => (
        <tr key={q.key}>
          <td className="border border-slate-300 px-2 py-1 font-mono text-slate-400">{i + 1}</td>
          <td className="border border-slate-300 px-2 py-1 font-medium">{q.label}</td>
          <td className="border border-slate-300 px-2 py-1" />
          {AREAS.map((a) => {
            const on = [q.primary, q.secondary].includes(a.key);
            return <td key={a.key} className={`border border-slate-300 px-2 py-1 ${on ? "" : "bg-slate-200"}`} />;
          })}
        </tr>
      ))}
      <tr className="font-semibold">
        <td className="border border-slate-300 px-2 py-1.5" colSpan={3}>Area total (out of 36)</td>
        {AREAS.map((a) => <td key={a.key} className="border border-slate-300 px-2 py-1.5" />)}
      </tr>
      <tr>
        <td className="border border-slate-300 px-2 py-1.5" colSpan={3}>Level (see key)</td>
        {AREAS.map((a) => <td key={a.key} className="border border-slate-300 px-2 py-1.5" />)}
      </tr>
    </tbody>
  </table>
);

export const ScoringKeys = () => (
  <div className="mt-4 grid grid-cols-2 gap-4">
    <div className="rounded-lg border border-slate-200 p-3">
      <div className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">Rating points</div>
      <ol className="mt-1 space-y-0.5 text-xs text-slate-700">
        {RATING_SCALE.map((r, i) => <li key={r.short}><span className="font-mono">{i}</span> · {r.full}</li>)}
      </ol>
    </div>
    <div className="rounded-lg border border-slate-200 p-3">
      <div className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">Level key (per area)</div>
      <ul className="mt-1 space-y-0.5 text-xs text-slate-700">
        {LEVELS.map((l, i) => (
          <li key={l.label} className="flex items-center gap-2">
            <span className="inline-block h-2.5 w-6 rounded" style={{ background: l.hex }} /> {l.label}: {ranges[i]}
          </li>
        ))}
        <li className="pt-1 text-slate-500">Overall: add the five totals and divide by 5, then use the same key.</li>
      </ul>
    </div>
  </div>
);

export default function DiyAssessment() {
  useResourcePrint();
  return (
    <div className="resource-doc mx-auto max-w-[8.5in]" data-testid="diy-assessment">
      <PrintBar title="DIY Assessment & Scoring Worksheet" />
      <DiyAssessmentContent />
    </div>
  );
}

export function DiyAssessmentContent() {
  const q = useQuestions();
  const [questions, setQuestions] = useState([]);
  useEffect(() => {
    api.get("/scoring").then((r) => setQuestions(r.data.questions)).catch(() => {});
  }, []);
  const desc = q?.descriptions || {};
  const pages = [questions.slice(0, 8), questions.slice(8, 17), questions.slice(17)];
  const startNo = [1, 9, 18];

  return (
    <>
      <section className="rounded-2xl border border-slate-200 bg-white p-8 shadow-sm print-plain">
        <div className="flex items-center gap-3">
          <img src="/flower-logo.png" alt="" className="h-10 w-10" />
          <div>
            <div className="text-[11px] font-mono uppercase tracking-widest text-slate-500">Minister's Helper · DIY Assessment</div>
            <h1 className="font-display text-2xl font-bold text-slate-900">Rate Yourself: 25 Topics</h1>
          </div>
        </div>
        <p className="mt-3 text-sm text-slate-600">
          For each topic, tick the one rating that best describes you today. The topics are in the same order as the online
          assessment, so you can copy your answers across easily. The number after each rating is its points.
        </p>
        <div className="mt-2 text-sm text-slate-700">Name: ____________________________ &nbsp; Date: ________________</div>
        <div className="mt-2">{pages[0].map((x, i) => <Question key={x.key} n={i + 1} q={x} desc={desc[x.key]} />)}</div>
      </section>
      {[1, 2].map((p) => (
        <section key={p} className="page-break mt-8 rounded-2xl border border-slate-200 bg-white p-8 shadow-sm print-plain">
          <div>{pages[p].map((x, i) => <Question key={x.key} n={i + startNo[p]} q={x} desc={desc[x.key]} />)}</div>
          {p === 2 && <ResourceNotice />}
        </section>
      ))}
      <section className="page-break mt-8 rounded-2xl border border-slate-200 bg-white p-8 shadow-sm print-plain" data-testid="diy-scoring-page">
        <h2 className="font-display text-xl font-bold text-slate-900">Score Yourself</h2>
        <p className="mt-1 text-sm text-slate-600">
          Write each topic's points (0–4) in the Points column, then copy the same number into every white box on that row. Some
          topics count toward two areas and get the same points in both. Add each column to get your area totals, then use the
          key to find your level and color your flower.
        </p>
        <ScoringTable questions={questions} />
        <ScoringKeys />
        <ResourceNotice />
      </section>
    </>
  );
}
